/** @module Specification/PartMaterialSize/SizeDataStoreTable */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"underscore",
	"csi",
	"csi/card/_base",
	"csi/card/AttributeCard",
	"csi/def",
	"csi/def/Action",
	"csi/html",
	"csi/locale",
	"csi/store/common",
	"csi/toolbar/ToolbarAttributeCard",
	"csi/view/ToolbarPluginRadioButton",
	"csi/view/ToolbarPluginSwitchboard",
	"csi/widget/FilteringSelect",
	"csi/xml",
	"Specification/SizeVariationParams",
	"../SizeVariationTable"
], function(declare, lang, _, csi, csiCard, AttributeCard, def, Action, html, locale, {DISABLED_PROPERTY}, ToolbarAttributeCard, ToolbarPluginRadioButton, ToolbarPluginSwitchboard, FilteringSelect, xml, SizeVariationParams, SizeVariationTable) {

	var PLUGIN_PREVIOUS = "BomSizeVariationPrevious";
	var PLUGIN_SELECT = "BomSizeVariationSelect";
	var PLUGIN_NEXT = "BomSizeVariationNext";

	function _getVariationName(sizeVariation) {
		return locale.typeId("PartMaterial", sizeVariation.composition);
	}

	function _getPushAction(fromVariation, toVariation, cssSuffix) {
		var extraCss = cssSuffix ? (" csi-toggle-group-" + cssSuffix) : "";

		return lang.mixin(new Action(), {
			bSecondaryToolbar: true,
			m_bToggle: fromVariation === toVariation,
			m_id: "BomSizeVariation" + toVariation.attr,
			m_name: _getVariationName(toVariation),
			m_onAction: "onActionSizeChanges",
			m_postAction: "postActionBOMVariation",
			classToolbarPlugin: ToolbarPluginRadioButton,
			classToolbarPluginParams: {
				bomSizeVariation: toVariation,
				radioClass: "bomSizeVariation",
				"class": "bold csi-toolbar-radio-button csi-button-toggle" + extraCss
			}
		});
	}

	var _PlacementSelect = declare(FilteringSelect, {
		dropDownCssClass: "groupedDropDown",
		labelType: "html",

		/** @property {Context} viewContext - view context */
		viewContext: null,

		constructor: function() {
			// override fetchProperties to group, but keep groups ordered
			this.fetchProperties = {
				group: {
					id: "group",
					sortFunc: null
				}
			};
		},

		labelFunc: function(item, store) {
			// for group items just return name
			if (item[DISABLED_PROPERTY]) {
				return item.name;
			}

			const {viewContext} = this;
			const pmUrl = store.getIdentity(item);
			const div = html.create("div");
			const cardParams = csiCard.getAttributeCardSlimSpecs("PartMaterial");
			const card = new AttributeCard({
				ctx: viewContext.setNodeURL(pmUrl),
				...cardParams,
			});
			card.placeAt(div);

			return div.innerHTML;
		},
	});

	return declare(SizeVariationTable, {
		useSecondaryToolbar: true,

		/** @property {boolean} bAlignToolbarWidth - make toolbar width equal to table width */
		bAlignToolbarWidth: true,

		/** @property {string} initialVariationNodeType - initial node type to display */
		initialVariationNodeType: null,

		/** @property {Object<string,boolean>} modifiedPlacements - map of placements that were changed */
		modifiedPlacements: null,

		/** @property {Memory} placementStore - data store of placements that vary by size */
		placementStore: null,

		/** @property {number} placementTotal - number of placements in store */
		placementTotal: 0,

		/**
		 * Return url for next or previous placement
		 * @param {boolean} [bPrevious]
		 * @return {string}
		 */
		getNextPlacement: function(bPrevious) {
			var store = this.placementStore;
			var item = store.get(this.urlRoot);
			var index = item.index + (bPrevious ? -1 : 1);
			var next = store.query({index: index}, {group: {}});
			return next[0].id;
		},

		/**
		 * Return true if there is a next (or previous) placement
		 * @param {boolean} [bPrevious]
		 * @return {boolean}
		 */
		hasNextPlacement: function(bPrevious) {
			var item = this.placementStore.get(this.urlRoot);

			if (bPrevious) {
				return item.index > 0;
			}

			return item.index + 1 < this.placementTotal;
		},

		/**
		 * @override
		 * Attribute change occurs on client node, and so no publish operation is invoked; instead,
		 * force change now
		 */
		onActionPaste: function(ac) {
			this._bValueChanged = true;
			this.onActionSizeChanges(ac);
		},

		/**
		 * Write any changes to writer
		 * @param {Controller} ac
		 */
		onActionSizeChanges: function(ac) {
			if (this._bValueChanged) {
				this.modifiedPlacements[this.urlRoot] = true;
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Add Qty/Size selectors to toolbar and ability to switch between placements
		 */
		onResolveSizes: function() {
			const {bomSizeSet, cacheView, initialVariationNodeType, plugins} = this;

			function addPlugin(action) {
				action.resolve();
				plugins.push(action);
			}

			const pm = this.getViewNode();
			const actual = cacheView.get(pm.Actual);
			let bomSizeVariation;

			// if no sizes, then switch to editing Quantity
			this.set("bomSizeVariation", _.isEmpty(actual.ProductSizes) ? SizeVariationParams.QTY_PARAMS : SizeVariationParams.SIZE_PARAMS);
			if (initialVariationNodeType) {
				// switch to desired variation
				bomSizeVariation = [SizeVariationParams.QTY_PARAMS, SizeVariationParams.SIZE_PARAMS, SizeVariationParams.SIZE_SPEC_PARAMS].find((params) => params.nodeType === initialVariationNodeType);
			} else {
				// if no sizes, then switch to editing Quantity
				bomSizeVariation = _.isEmpty(actual.ProductSizes) ? SizeVariationParams.QTY_PARAMS : SizeVariationParams.SIZE_PARAMS;
			}

			this.set("bomSizeVariation", bomSizeVariation);
			this.set("column1Name", locale.resource("BOMSize"));
			this.set("column2Name", _getVariationName(bomSizeVariation));
			this.modifiedPlacements = {};

			// add plugin to show current placement card
			addPlugin(lang.mixin(new Action(), {
				m_id: "PlacementCard",
				classToolbarPlugin: ToolbarAttributeCard,
			}));

			// add plugins for switching placements
			var store = this.placementStore;
			if (store) {
				// reverse order since floating right
				addPlugin(lang.mixin(new Action(), {
					m_id: PLUGIN_NEXT,
					m_onAction: "onActionSizeChanges",
					m_postAction: "postActionBOMPlacementRight",
					m_typeControl: def.ACTION_ICON,
					materialIcon: "keyboard_arrow_right",
					classToolbarPluginParams: {
						"class": "csi-toolbar-btn-right",
						_hasAccessToAction: (ctx) => !!ctx.view.hasNextPlacement(),
					}
				}));
				addPlugin(lang.mixin(new Action(), {
					m_id: PLUGIN_SELECT,
					m_onAction: "onActionSizeChanges",
					m_postAction: "postActionBOMPlacement",
					classToolbarPlugin: ToolbarPluginSwitchboard,
					classToolbarPluginParams: {
						"class": "csi-toolbar-btn-right",
						idViewProperty: "urlRoot",
						selectClass: _PlacementSelect,
						showLabel: false,
						store,
						onChangeItem: () => { },
						onSelectParams: (params) => {
							params.viewContext = this.getContext(csi.EMPTY_URL);
							params.viewContext.bCaptureHtml = true;
						},
					}
				}));
				addPlugin(lang.mixin(new Action(), {
					m_id: PLUGIN_PREVIOUS,
					m_onAction: "onActionSizeChanges",
					m_postAction: "postActionBOMPlacementLeft",
					m_typeControl: def.ACTION_ICON,
					materialIcon: "keyboard_arrow_left",
					classToolbarPluginParams: {
						"class": "csi-toolbar-btn-right",
						_hasAccessToAction: (ctx) => !!ctx.view.hasNextPlacement(true),
					}
				}));
			}

			// add toolbar plugins to toggle between Size and Quantity
			if (bomSizeSet.has2dSizes()) {
				addPlugin(_getPushAction(bomSizeVariation, SizeVariationParams.SIZE_PARAMS, "first"));
				addPlugin(_getPushAction(bomSizeVariation, SizeVariationParams.QTY_PARAMS, "second"));
				addPlugin(_getPushAction(bomSizeVariation, SizeVariationParams.SIZE_SPEC_PARAMS, "last"));
			}
		},

		/**
		 * Switch to the previous placement
		 * @param {Controller} ac
		 * @return {Deferred}
		 */
		postActionBOMPlacement: function(ac) {
			return this.setBOMPlacement(ac.actionValue);
		},

		/**
		 * Switch to the previous placement
		 * @param {Controller} ac
		 * @return {Deferred}
		 */
		postActionBOMPlacementLeft: function(ac) {
			return this.setBOMPlacement(this.getNextPlacement(true));
		},

		/**
		 * Switch to the next placement
		 * @param {Controller} ac
		 * @return {Deferred}
		 */
		postActionBOMPlacementRight: function(ac) {
			return this.setBOMPlacement(this.getNextPlacement());
		},

		/**
		 * Change size variation and refresh view
		 * @param {Controller} ac
		 * @return {Deferred}
		 */
		postActionBOMVariation: function(ac) {
			var sizeVariation = ac.actionWidget.bomSizeVariation;
			this.set("bomSizeVariation", sizeVariation);
			this.set("column2Name", _getVariationName(sizeVariation));
			this._updateColumn2Name();
			this.postSizeChanges();
			this.refreshAttributes();
			return this.redrawNewData();
		},

		/**
		 * Track changes to PartMaterial and reset variables tracking changes
		 */
		postSizeChanges: function() {
			this.variationNodeMap = {};
			this._bValueChanged = false;
		},

		/**
		 * Change BOM placement for view
		 * @param {string} url
		 */
		setBOMPlacement: function(url) {
			this.postSizeChanges();
			this.setViewNode(this.cacheView.get(url));
			return this.refreshData();
		},

		/**
		 * @override
		 * Add dependency path to get actual node and size variations
		 */
		_onRootDependencyPaths: function(paths) {
			paths.push("Child:Actual;PartMaterialQuantities;PartMaterialSizes;PartMaterialSizeSpecs");
		}
	});
});